// Conflict detection, which is the product's center of gravity.
//
// A contested window is an interval where members' independently solved plans put
// them at different stages. Detection is a walk down the merged timeline of those
// plans; adjacent intervals sharing the same divergence collapse into one card.
//
// Cards come out ranked by severity and never chronologically. Nobody has the
// patience to walk forty overlaps in time order, and the artifact a crew can
// actually act on is the five decisions that matter this weekend.
//
//     severity = spread * reach^e * lockFactor * durationFactor
//
// Compromise is generated first and offered first. Most festival conflicts are not
// either-or, and a tool that only ever offers a binary choice is worse than the
// group chat it replaces. A compromise is only offered when the walk makes it
// physically possible and the partial-attendance curves make it worth more than
// either whole set.

import type { TuningConfig } from './config.ts';
import { DEFAULT_CONFIG } from './config.ts';
import type { GroupUtilityInput } from './group.ts';
import { memberValue } from './group.ts';
import { bandOf } from './normalize.ts';
import { attendanceMultiplier } from './partial.ts';
import { pairCandidates, validateRoute } from './planning.ts';
import type { RouteBlock } from './planning.ts';
import { walkMs } from './walk.ts';
import { fmtRange, fmtTime, overlapMs } from './time.ts';
import type {
  Conflict,
  ConflictOption,
  FestivalSet,
  Id,
  MemberPosition,
  Note,
  Plan,
  Stage,
  WalkMatrix,
  WorkspaceSettings,
} from './types.ts';

export interface ConflictInput {
  workspaceId: Id;
  dayId: Id;
  /** One solved plan per member, all for the same day. */
  plans: { userId: Id; displayName: string; plan: Plan; hardLockSetIds: Id[] }[];
  sets: FestivalSet[];
  stages: Stage[];
  walk: WalkMatrix;
  group: GroupUtilityInput;
  settings: WorkspaceSettings;
  notes?: Note[];
  cfg?: TuningConfig;
}

interface Divergence {
  start: number;
  end: number;
  /** Where each member is, by user id. An empty stage means nowhere in particular. */
  at: Map<Id, { stageId: string; setId?: Id }>;
}

/** Finds every window where the crew's independent plans put them in two places. */
export function detectDivergences(input: ConflictInput): Divergence[] {
  const marks = new Set<number>();
  for (const p of input.plans) {
    for (const b of p.plan.blocks) {
      marks.add(b.startTs);
      marks.add(b.endTs);
    }
  }
  const bp = [...marks].sort((a, b) => a - b);
  const raw: Divergence[] = [];

  for (let i = 0; i < bp.length - 1; i++) {
    const start = bp[i];
    const end = bp[i + 1];
    const at = new Map<Id, { stageId: string; setId?: Id }>();
    for (const p of input.plans) {
      const block = p.plan.blocks.find((b) => b.startTs <= start && b.endTs >= end && b.mode !== 'travel');
      if (block) at.set(p.userId, { stageId: block.stageId, setId: block.setId });
    }
    const stages = new Set([...at.values()].map((v) => v.stageId));
    if (stages.size > 1) raw.push({ start, end, at });
  }

  // Merge neighbors that describe the same disagreement.
  const merged: Divergence[] = [];
  for (const d of raw) {
    const prev = merged[merged.length - 1];
    if (prev && prev.end === d.start && sameDivergence(prev.at, d.at)) {
      prev.end = d.end;
      continue;
    }
    merged.push({ ...d, at: new Map(d.at) });
  }
  return merged;
}

function sameDivergence(
  a: Map<Id, { stageId: string; setId?: Id }>,
  b: Map<Id, { stageId: string; setId?: Id }>,
): boolean {
  if (a.size !== b.size) return false;
  for (const [k, v] of a) {
    const other = b.get(k);
    if (!other || other.stageId !== v.stageId || other.setId !== v.setId) return false;
  }
  return true;
}

/** Value of one member attending part of one set, on the comparable scale. */
function memberSpanValue(
  input: ConflictInput,
  userId: Id,
  set: FestivalSet,
  startTs: number,
  endTs: number,
): number {
  const cfg = input.cfg ?? DEFAULT_CONFIG;
  const view = input.group.views.find((v) => v.userId === userId);
  if (!view) return 0;
  const mv = memberValue(view, set.artistId);
  if (!mv) return 0;
  return mv.value * mv.weight * attendanceMultiplier(set.startTs, set.endTs, startTs, endTs, cfg);
}

function optionValue(
  input: ConflictInput,
  segments: { setId: Id; startTs: number; endTs: number }[],
): { group: number; perMember: Record<Id, number> } {
  const perMember: Record<Id, number> = {};
  for (const p of input.plans) {
    let total = 0;
    for (const seg of segments) {
      const set = input.sets.find((s) => s.id === seg.setId);
      if (!set) continue;
      total += memberSpanValue(input, p.userId, set, seg.startTs, seg.endTs);
    }
    perMember[p.userId] = total;
  }
  const weights = input.group.weights;
  let sum = 0;
  let wTotal = 0;
  for (const p of input.plans) {
    const w = weights.get(p.userId) ?? 1;
    sum += w * perMember[p.userId];
    wTotal += w;
  }
  return { group: wTotal > 0 ? sum / wTotal : 0, perMember };
}

/** Builds every resolution worth offering for one contested window. */
export function buildOptions(input: ConflictInput, d: Divergence): ConflictOption[] {
  const cfg = input.cfg ?? DEFAULT_CONFIG;
  const contestedSetIds = [...new Set([...d.at.values()].map((v) => v.setId).filter(Boolean))] as Id[];
  const contested = contestedSetIds
    .map((id) => input.sets.find((s) => s.id === id))
    .filter((s): s is FestivalSet => Boolean(s));

  const options: ConflictOption[] = [];

  for (const set of contested) {
    const segs = [{ setId: set.id, startTs: set.startTs, endTs: set.endTs }];
    const v = optionValue(input, segs);
    options.push({
      id: `full:${set.id}`,
      kind: 'full',
      label: `Everyone at ${stageName(input, set.stageId)} for the whole set`,
      segments: segs,
      groupValue: v.group,
      perMember: v.perMember,
      feasible: true,
    });
  }

  // Cutoffs are enumerated in both directions at a fixed step. A single cut at
  // the earlier of the two end times puts arrival past the end of the second
  // set whenever they finish together, which reports an ordinary
  // first-half-then-walk plan as impossible.
  const walkFor = (from: Id, to: Id) => walkMs(input.walk, from, to) / 60000;
  const seen = new Set<string>();

  for (let i = 0; i < contested.length; i++) {
    for (let j = i + 1; j < contested.length; j++) {
      const a = contested[i];
      const b = contested[j];
      const travelMinutes = walkFor(a.stageId, b.stageId);

      let candidates: RouteBlock[][];
      try {
        candidates = pairCandidates(a, b, travelMinutes, { minimumMinutes: 15, stepMinutes: 5 });
      } catch {
        candidates = [];
      }

      // Each candidate is checked as a whole route rather than as two blocks
      // that happen to be adjacent.
      const scored = candidates
        .filter((route) => validateRoute(route, { sets: input.sets, walkMinutes: walkFor }).feasible)
        .map((route) => {
          const segs = route.map((r) => ({
            setId: r.setId!,
            startTs: r.startTs,
            endTs: r.endTs,
          }));
          return { segs, value: optionValue(input, segs) };
        });

      if (scored.length === 0) {
        const id = `compromise:${a.id}:${b.id}`;
        if (seen.has(id)) continue;
        seen.add(id);
        options.push({
          id,
          kind: 'compromise',
          label: 'Part of one, then the other',
          segments: [],
          groupValue: 0,
          perMember: {},
          feasible: false,
          infeasibleReason: `The walk is ${Math.round(travelMinutes)} minutes and no cutoff leaves fifteen usable minutes at both.`,
        });
        continue;
      }

      // One card per ordering. Every cutoff in between is a variation on the
      // same decision, and forty of them on one card is unreadable.
      const bestByOrder = new Map<Id, (typeof scored)[number]>();
      for (const candidate of scored) {
        const firstSetId = candidate.segs[0].setId;
        const held = bestByOrder.get(firstSetId);
        if (!held || candidate.value.group > held.value.group) bestByOrder.set(firstSetId, candidate);
      }

      for (const [firstSetId, candidate] of bestByOrder) {
        const secondSetId = candidate.segs[1].setId;
        const first = input.sets.find((s) => s.id === firstSetId)!;
        const second = input.sets.find((s) => s.id === secondSetId)!;
        const id = `compromise:${firstSetId}:${secondSetId}`;
        if (seen.has(id)) continue;
        seen.add(id);
        options.push({
          id,
          kind: 'compromise',
          label:
            `${setLabel(input, first)} until ${fmtTime(candidate.segs[0].endTs)}, ` +
            `then ${setLabel(input, second)} from ${fmtTime(candidate.segs[1].startTs)}`,
          segments: candidate.segs,
          groupValue: candidate.value.group,
          perMember: candidate.value.perMember,
          feasible: true,
        });
      }
    }
  }

  // Splitting means every member keeps what their own solve chose.
  const splitPer: Record<Id, number> = {};
  for (const [userId, where] of d.at) {
    const set = where.setId ? input.sets.find((s) => s.id === where.setId) : undefined;
    splitPer[userId] = set ? memberSpanValue(input, userId, set, d.start, d.end) : 0;
  }
  let splitSum = 0;
  let splitW = 0;
  for (const p of input.plans) {
    const w = input.group.weights.get(p.userId) ?? 1;
    splitSum += w * (splitPer[p.userId] ?? 0);
    splitW += w;
  }
  options.push({
    id: 'split',
    kind: 'split',
    label: 'Split up and meet after',
    segments: [],
    groupValue: splitW > 0 ? splitSum / splitW : 0,
    perMember: splitPer,
    feasible: true,
  });

  void cfg;
  return options;
}

function stageName(input: ConflictInput, stageId: Id): string {
  return input.stages.find((s) => s.id === stageId)?.name ?? 'that stage';
}

function setLabel(input: ConflictInput, set: FestivalSet): string {
  return stageName(input, set.stageId);
}

/** Scores one contested window so the cards can be ranked by what matters. */
export function severityOf(
  input: ConflictInput,
  d: Divergence,
  options: ConflictOption[],
): { severity: number; parts: Conflict['severityParts']; lockCollision: boolean } {
  const cfg = input.cfg ?? DEFAULT_CONFIG;
  const values: number[] = [];
  for (const opt of options) {
    if (!opt.feasible || opt.kind !== 'full') continue;
    for (const p of input.plans) values.push(opt.perMember[p.userId] ?? 0);
  }
  const spread = values.length > 0 ? Math.max(...values) - Math.min(...values) : 0;

  const affected = d.at.size;
  const reach = Math.pow(Math.max(1, affected), cfg.severityReachExponent);

  const locksHeld = input.plans.filter((p) =>
    p.hardLockSetIds.some((id) => {
      const set = input.sets.find((s) => s.id === id);
      return set ? overlapMs(set.startTs, set.endTs, d.start, d.end) > 0 : false;
    }),
  );
  const lockCollision = locksHeld.length > 1 && new Set(
    locksHeld.map((p) => {
      const set = input.sets.find((s) => p.hardLockSetIds.includes(s.id) && overlapMs(s.startTs, s.endTs, d.start, d.end) > 0);
      return set?.stageId;
    }),
  ).size > 1;

  const lockFactor = lockCollision
    ? cfg.severityLockCollisionFactor
    : locksHeld.length > 0
      ? cfg.severityLockFactor
      : 1;

  const durationFactor = Math.min(3, Math.max(0.25, (d.end - d.start) / cfg.severityDurationHalfLifeMs));

  return {
    severity: spread * reach * lockFactor * durationFactor,
    parts: { spread, reach, lockFactor, durationFactor },
    lockCollision,
  };
}

function positionsFor(input: ConflictInput, d: Divergence, options: ConflictOption[]): MemberPosition[] {
  return input.plans.map((p) => {
    const view = input.group.views.find((v) => v.userId === p.userId);
    const where = d.at.get(p.userId);
    const set = where?.setId ? input.sets.find((s) => s.id === where.setId) : undefined;
    const n = view && set ? view.normalized.get(set.artistId) : undefined;
    const note = input.notes?.find((x) => x.userId === p.userId && set && x.artistId === set.artistId);
    const best = Math.max(0, ...options.filter((o) => o.feasible).map((o) => o.perMember[p.userId] ?? 0));
    return {
      userId: p.userId,
      displayName: p.displayName,
      band: bandOf(n?.value ?? 0),
      rawScore: input.settings.scoreVisibility === 'raw' ? n?.rawScore : undefined,
      normalized: n?.value ?? 0,
      hasHardLock: p.hardLockSetIds.some((id) => id === set?.id),
      preferredSetId: set?.id,
      noteExcerpt: note?.body ? note.body.slice(0, 140) : undefined,
      bestAvailable: best,
    } as MemberPosition & { bestAvailable: number };
  });
}

/** Produces the ranked conflict cards for one day. */
export function detectConflicts(input: ConflictInput): Conflict[] {
  const cfg = input.cfg ?? DEFAULT_CONFIG;
  const out: Conflict[] = [];

  for (const d of detectDivergences(input)) {
    if (d.end - d.start < cfg.severityMinWindowMs) continue;
    const options = buildOptions(input, d);
    const { severity, parts, lockCollision } = severityOf(input, d, options);
    if (severity <= 0) continue;

    const feasible = options.filter((o) => o.feasible);
    const best = feasible.reduce((a, b) => (b.groupValue > a.groupValue ? b : a), feasible[0]);
    // Compromise wins a near-tie, because it is the answer a group rarely finds on
    // its own and the one that leaves nobody having lost.
    const compromise = feasible
      .filter((o) => o.kind === 'compromise')
      .reduce<ConflictOption | undefined>((a, b) => (!a || b.groupValue > a.groupValue ? b : a), undefined);
    const recommended =
      compromise && compromise.groupValue >= best.groupValue * 0.97 ? compromise : best;

    out.push({
      id: `conflict:${input.dayId}:${d.start}`,
      workspaceId: input.workspaceId,
      dayId: input.dayId,
      windowStart: d.start,
      windowEnd: d.end,
      severity,
      severityParts: parts,
      status: lockCollision ? 'voting' : 'open',
      options,
      recommendedOptionId: recommended?.id,
      recommendationReason: reasonFor(input, recommended, lockCollision, d),
      lockCollision,
      memberPositions: positionsFor(input, d, options),
    });
  }

  return out.sort((a, b) => b.severity - a.severity);
}

function reasonFor(
  input: ConflictInput,
  option: ConflictOption | undefined,
  lockCollision: boolean,
  d: Divergence,
): string {
  if (lockCollision) {
    return `Two locked sets collide between ${fmtRange(d.start, d.end)}. Weighting cannot settle this one, so it goes to a vote or a split.`;
  }
  if (!option) return 'No option is reachable in this window.';
  if (option.kind === 'compromise') {
    return 'The walk makes both reachable, and catching one closing and the other opening is worth more to the crew than either set taken whole.';
  }
  if (option.kind === 'split') {
    return 'Nobody gains enough from converging to pay for the walk, so splitting costs the crew least.';
  }
  const leader = input.plans
    .map((p) => ({ name: p.displayName, v: option.perMember[p.userId] ?? 0 }))
    .sort((a, b) => b.v - a.v)[0];
  return leader
    ? `This is the strongest option for the crew as weighted, and ${leader.name} gains most from it.`
    : 'This is the strongest option for the crew as weighted.';
}
